<div class="fx-table-container">
    <!--
        A functional imbalance invalidates every figure in this component, not just the proof — so it
        is announced above the tabs rather than only inside Kontrolle, where someone working in the
        Bilanz would never see it.
    -->
    <div class="fx-imbalance-banner" *ngIf="functionalImbalances.length > 0">
        <span
            hamIconShapes
            icon="assets/hamIcons/x-red.svg"
            [variant]="'dark-red'"
            [size]="'xs'"
            class="fx-imbalance-icon"
        ></span>
        <div>
            <div class="fx-imbalance-title">{{ 'aumico_annual_report_fx_proof_imbalance_banner' | i18next }}</div>
            <div class="fx-imbalance-detail" *ngFor="let year of functionalImbalances">
                {{ year.header }}: {{ functionalCurrencyName }} {{ year.delta | number : '1.2-2' }}
            </div>
        </div>
    </div>

    <div class="fx-table-nav">
        <ham-tab-navigation
            [navigationItems]="navigationItems"
            [tabsTitles]="[
                'aumico_annual_report_import_form_balance_title' | i18next,
                'aumico_annual_report_import_form_income_title' | i18next,
                'aumico_annual_report_fx_table_tab_kontrolle' | i18next
            ]"
            [style]="'underline'"
            [color]="'primary'"
            (tabClick)="onTabClick($event)"
        ></ham-tab-navigation>
    </div>

    <ng-container *ngIf="selectedTab === tabBalance">
        <ng-container
            *ngTemplateOutlet="statementTable; context: { root: balanceSheet, isBalance: true }"
        ></ng-container>
    </ng-container>
    <ng-container *ngIf="selectedTab === tabIncome">
        <ng-container
            *ngTemplateOutlet="statementTable; context: { root: incomeStatement, isBalance: false }"
        ></ng-container>
    </ng-container>
    <ng-container *ngIf="selectedTab === tabKontrolle">
        <!-- Verdict first: the panels below are the evidence, and only needed once something is
             red or someone is auditing (03d §3). -->
        <div class="fx-checks">
            <div
                class="fx-check"
                *ngFor="let check of checks"
                [class.is-failed]="!check.ok && !check.warning"
                [class.is-warning]="!check.ok && check.warning"
                [class.is-actionable]="check.roundingYear != null"
                (click)="check.roundingYear != null && openRoundingDialog(check)"
            >
                <div class="fx-check-top">
                    <!-- Same badge pair the print preview's consistency checks use. -->
                    <span
                        *ngIf="check.ok"
                        hamIconShapes
                        icon="assets/hamIcons/check-outline.svg"
                        [variant]="'dark-green'"
                        [size]="'xs'"
                        class="fx-check-icon"
                    ></span>
                    <span
                        *ngIf="!check.ok && check.warning"
                        hamIconShapes
                        icon="assets/hamIcons/exclamation-outline.svg"
                        [variant]="'yellow'"
                        [size]="'xs'"
                        class="fx-check-icon"
                    ></span>
                    <span
                        *ngIf="!check.ok && !check.warning"
                        hamIconShapes
                        icon="assets/hamIcons/x-red.svg"
                        [variant]="'dark-red'"
                        [size]="'xs'"
                        class="fx-check-icon"
                    ></span>
                    <span class="fx-check-label">{{ check.label }}</span>
                </div>
                <div class="fx-check-detail" *ngIf="!check.reason">
                    {{ check.left | number : '1.2-2' }}
                    <span class="fx-check-eq">=</span>
                    {{ check.right | number : '1.2-2' }}
                    <!-- The tolerance may pass the check, but it never hides the gap (03d S1). -->
                    <em class="fx-check-diff" *ngIf="check.difference !== 0">
                        Δ {{ check.difference | number : '1.2-2' }}
                    </em>
                </div>
                <div class="fx-check-detail" *ngIf="check.reason">{{ check.reason }}</div>
                <div class="fx-check-detail fx-check-note" *ngIf="check.note">{{ check.note }}</div>
                <div class="fx-check-detail fx-check-action" *ngIf="check.action">{{ check.action }} →</div>
            </div>

            <div
                class="fx-check is-info"
                *ngIf="proofCY != null"
                [class.is-failed]="proofCY.rounding >= roundingTolerance || proofCY.rounding <= -roundingTolerance"
            >
                <div class="fx-check-top">
                    <!-- Gray, not green: rounding is information, never a verdict (AC 3d.2) — until
                         it passes a franc, where it stops being plausible as rounding (03d §5). -->
                    <span
                        *ngIf="proofCY.rounding < roundingTolerance && proofCY.rounding > -roundingTolerance"
                        hamIconShapes
                        icon="assets/hamIcons/info-outline.svg"
                        [variant]="'gray'"
                        [size]="'xs'"
                        class="fx-check-icon"
                    ></span>
                    <span
                        *ngIf="proofCY.rounding >= roundingTolerance || proofCY.rounding <= -roundingTolerance"
                        hamIconShapes
                        icon="assets/hamIcons/x-red.svg"
                        [variant]="'dark-red'"
                        [size]="'xs'"
                        class="fx-check-icon"
                    ></span>
                    <span class="fx-check-label">{{ 'aumico_annual_report_fx_proof_rounding' | i18next }}</span>
                </div>
                <div class="fx-check-detail">
                    {{ displayCurrencyName }} {{ proofCY.rounding | number : '1.2-2' }}
                    <em class="fx-check-diff">{{ 'aumico_annual_report_fx_proof_rounding_hint' | i18next }}</em>
                </div>
            </div>
        </div>

        <!-- Without period rates there is nothing to prove and nothing to check — say so rather
             than leaving the tab empty. -->
        <div class="fx-table-placeholder" *ngIf="proofCY == null">
            {{ 'aumico_annual_report_fx_table_kontrolle_placeholder' | i18next }}
        </div>

        <ng-container *ngTemplateOutlet="proofPanel; context: { proof: proofCY, current: true }"></ng-container>
        <ng-container *ngTemplateOutlet="proofPanel; context: { proof: proofPY, current: false }"></ng-container>
    </ng-container>
</div>

<ng-template #statementTable let-root="root" let-isBalance="isBalance">
    <div class="fx-grid" *ngIf="root">
        <!-- Year band: label spacer + one colspan-3 block per year -->
        <div class="fx-year-band">
            <div class="fx-year-spacer"></div>
            <div class="fx-year-block current">
                {{ isBalance ? headerBalanceCY : headerIncomeCY }}
            </div>
            <div class="fx-year-block previous">
                {{ isBalance ? headerBalancePY : headerIncomePY }}
            </div>
        </div>

        <!-- Column header row -->
        <div class="grid-row fx-cols-header">
            <div class="col-name">{{ 'aumico_annual_report_fx_table_position' | i18next }}</div>
            <div class="col-val col-cy">{{ functionalCurrencyName }}</div>
            <div class="col-method col-cy">{{ 'aumico_annual_report_fx_table_kurstyp' | i18next }}</div>
            <div class="col-val col-cy">{{ displayCurrencyName }}</div>
            <div class="col-val col-py">{{ functionalCurrencyName }}</div>
            <div class="col-method col-py">{{ 'aumico_annual_report_fx_table_kurstyp' | i18next }}</div>
            <div class="col-val col-py">{{ displayCurrencyName }}</div>
        </div>

        <div class="fx-table-body">
            <ng-container
                *ngTemplateOutlet="nodeList; context: { items: root.sortedItems, level: 0 }"
            ></ng-container>

            <!-- The income statement's root group IS the year result, so it is rendered here as the
                 closing line rather than as a header above its own sections. -->
            <div class="grid-row fx-reserve-row" *ngIf="!isBalance">
                <div class="col-name">{{ resultLabel }}</div>
                <div class="col-val col-cy">{{ resultUsdCY | number : '1.2-2' }}</div>
                <div class="col-method col-cy rate-static">{{ resultRateCY }}</div>
                <div class="col-val col-cy">
                    <ng-container
                        *ngTemplateOutlet="aggregateAmount; context: { value: resultChfCY, provisional: resultProvisionalCY }"
                    ></ng-container>
                </div>
                <div class="col-val col-py">{{ resultUsdPY | number : '1.2-2' }}</div>
                <div class="col-method col-py rate-static">{{ resultRatePY }}</div>
                <div class="col-val col-py">
                    <ng-container
                        *ngTemplateOutlet="aggregateAmount; context: { value: resultChfPY, provisional: resultProvisionalPY }"
                    ></ng-container>
                </div>
            </div>

            <!-- The Translation Reserve: display-currency only, derived, never persisted. -->
            <div class="grid-row fx-reserve-row" *ngIf="isBalance && reserveCY != null">
                <div class="col-name">{{ 'aumico_annual_report_fx_table_reserve' | i18next }}</div>
                <div class="col-val col-cy"></div>
                <div class="col-method col-cy"></div>
                <div class="col-val col-cy">
                    <ng-container
                        *ngTemplateOutlet="aggregateAmount; context: { value: reserveCY, provisional: reserveProvisionalCY }"
                    ></ng-container>
                </div>
                <div class="col-val col-py"></div>
                <div class="col-method col-py"></div>
                <div class="col-val col-py">
                    <ng-container
                        *ngTemplateOutlet="aggregateAmount; context: { value: reservePY, provisional: reserveProvisionalPY }"
                    ></ng-container>
                </div>
            </div>
        </div>
    </div>
</ng-template>

<!-- Recursive walk over groups / leaves — same traversal order as Grouping & Masking. -->
<ng-template #nodeList let-items="items" let-level="level">
    <ng-container *ngFor="let item of items">
        <ng-container *ngIf="item != null && !item.__fx?.hidden">
            <!-- GROUP — including empty ones, which still carry a computed figure. -->
            <ng-container *ngIf="item.__fxIsGroup">
                <div
                    class="grid-row"
                    [ngClass]="level === 0 ? 'section-header-row' : 'group-row'"
                    [style.--level]="level"
                    (click)="toggleCollapse(item.__fxPath)"
                >
                    <div class="col-name">
                        <span
                            *ngIf="level > 0 && item.sortedItems?.length"
                            class="ham-icon chevron"
                            [class.expanded]="!isCollapsed(item.__fxPath)"
                        >
                            <i class="chevron-right-icon"></i>
                        </span>
                        <span class="title">{{ item.title }}</span>
                    </div>
                    <ng-container *ngTemplateOutlet="aggregateCells; context: { fx: item.__fx }"></ng-container>
                </div>
                <ng-container *ngIf="!isCollapsed(item.__fxPath)">
                    <ng-container
                        *ngTemplateOutlet="nodeList; context: { items: item.sortedItems || [], level: level + 1 }"
                    ></ng-container>
                </ng-container>
            </ng-container>

            <!-- LEAVES -->
            <ng-container *ngIf="!item.__fxIsGroup" [ngSwitch]="item.type">
                <!-- FLI: the total row of its accounts -->
                <ng-container *ngSwitchCase="ItemType.FLI">
                    <ng-container *ngIf="item.__fxPath as fliPath">
                        <div class="grid-row fli-row" [style.--level]="level" (click)="toggleCollapse(fliPath)">
                            <div class="col-name">
                                <span class="ham-icon chevron" [class.expanded]="!isCollapsed(fliPath)">
                                    <i class="chevron-right-icon"></i>
                                </span>
                                {{ item.caption || item.name }}
                            </div>
                            <ng-container *ngTemplateOutlet="aggregateCells; context: { fx: item.__fx }"></ng-container>
                        </div>
                        <ng-container *ngIf="!isCollapsed(fliPath)">
                            <ng-container
                                *ngTemplateOutlet="
                                    accountList;
                                    context: { accounts: item.accounts, reclasses: item.reclassesAccounts, level: level + 1 }
                                "
                            ></ng-container>
                        </ng-container>
                    </ng-container>
                </ng-container>

                <!-- Subtotal -->
                <div *ngSwitchCase="ItemType.SUB_TOTAL" class="grid-row subtotal-row" [style.--level]="level">
                    <div class="col-name">{{ item.caption || item.name }}</div>
                    <ng-container *ngTemplateOutlet="aggregateCells; context: { fx: item.__fx }"></ng-container>
                </div>

                <!-- Header / empty structural rows: value-less, but the cells still render so the
                     current-year tint stays a continuous block down the table. -->
                <div *ngSwitchCase="ItemType.HEADER" class="grid-row title-row" [style.--level]="level">
                    <div class="col-name">{{ item.caption || item.name }}</div>
                    <ng-container *ngTemplateOutlet="emptyCells"></ng-container>
                </div>
                <div *ngSwitchCase="ItemType.EMPTY" class="grid-row title-row" [style.--level]="level">
                    <div class="col-name">&nbsp;</div>
                    <ng-container *ngTemplateOutlet="emptyCells"></ng-container>
                </div>
            </ng-container>
        </ng-container>
    </ng-container>
</ng-template>

<!-- Accounts (and nested Sub-FLI / davon structures) inside one FLI. -->
<ng-template #accountList let-accounts="accounts" let-reclasses="reclasses" let-level="level">
    <ng-container *ngFor="let acc of accounts">
        <ng-container *ngIf="acc != null && !acc.__fx?.hidden">
            <!-- Sub-FLI / davon: a structural header plus its own accounts -->
            <ng-container *ngIf="acc.type === ItemType.SUB_FLI || acc.type === ItemType.OF_THAT_ITEM">
                <ng-container *ngIf="acc.__fxPath as subPath">
                    <div class="grid-row fli-row sub-fli" [style.--level]="level" (click)="toggleCollapse(subPath)">
                        <div class="col-name">
                            <span class="ham-icon chevron" [class.expanded]="!isCollapsed(subPath)">
                                <i class="chevron-right-icon"></i>
                            </span>
                            <span *ngIf="acc.type === ItemType.OF_THAT_ITEM" class="davon-prefix">
                                {{ 'aumico_templates_group_account_mapping_of_that_item_text' | i18next }}
                            </span>
                            {{ acc.caption || acc.name }}
                        </div>
                        <ng-container *ngTemplateOutlet="aggregateCells; context: { fx: acc.__fx }"></ng-container>
                    </div>
                    <ng-container *ngIf="!isCollapsed(subPath)">
                        <!-- Sub-FLIs carry their own phantom reclass rows — render them like at FLI level. -->
                        <ng-container
                            *ngTemplateOutlet="
                                accountList;
                                context: { accounts: acc.accounts, reclasses: acc.reclassesAccounts, level: level + 1 }
                            "
                        ></ng-container>
                    </ng-container>
                </ng-container>
            </ng-container>

            <!-- Plain account row -->
            <ng-container *ngIf="acc.type !== ItemType.SUB_FLI && acc.type !== ItemType.OF_THAT_ITEM">
                <div class="grid-row account-row" [style.--level]="level">
                    <div class="col-name">
                        <span class="acc-number">{{ accountNumberOf(acc) }}</span>
                        <span class="acc-name">{{ acc.name }}</span>
                    </div>
                    <div class="col-val col-cy">{{ acc.__fx?.usdCY | number : '1.2-2' }}</div>
                    <div class="col-method col-cy">
                        <ng-container
                            *ngTemplateOutlet="
                                methodCell;
                                context: { year: 'currentYear', ctrl: acc.__fxCtrlCY }
                            "
                        ></ng-container>
                    </div>
                    <div class="col-val col-cy">
                        <!-- Blocked by a missing prior year: the work belongs in the prior-year
                             cell, so this one stays inert — warning icon, explained on hover. -->
                        <ng-container *ngIf="acc.__fx?.blockedCY">
                            <ng-container
                                *ngTemplateOutlet="warningIcon; context: { hint: labels.blockedHint }"
                            ></ng-container>
                        </ng-container>
                        <!-- Historical and workable: the cell itself is the way into the editor. -->
                        <div
                            *ngIf="acc.__fx?.historicalCY && !acc.__fx?.blockedCY"
                            class="fx-hist-cell"
                            [class.is-open]="acc.__fx?.openCY"
                            (click)="onEditHistorical(acc, 'currentYear')"
                        >
                            <span class="fx-hist-add" aria-hidden="true">+</span>
                            <span *ngIf="acc.__fx?.openCY">
                                {{ (acc.__fx?.unexplainedCY | number : '1.2-2') + ' ' + labels.openSuffix }}
                            </span>
                            <span *ngIf="!acc.__fx?.openCY">{{ acc.__fx?.chfCY | number : '1.2-2' }}</span>
                        </div>
                        <span
                            *ngIf="!acc.__fx?.historicalCY"
                            [class.fx-rounding-booked]="acc.__fx?.roundingCY != null"
                            [attr.title]="
                                acc.__fx?.roundingCY != null
                                    ? labels.roundingCellHint + ' ' + (acc.__fx?.roundingCY | number : '1.2-2')
                                    : null
                            "
                            >{{ acc.__fx?.chfCY | number : '1.2-2' }}</span
                        >
                    </div>
                    <div class="col-val col-py">{{ acc.__fx?.usdPY | number : '1.2-2' }}</div>
                    <div class="col-method col-py">
                        <ng-container
                            *ngTemplateOutlet="
                                methodCell;
                                context: { year: 'previousYear', ctrl: acc.__fxCtrlPY }
                            "
                        ></ng-container>
                    </div>
                    <div class="col-val col-py">
                        <!-- Same editor, entered from whichever year the user happens to be reading. -->
                        <div
                            *ngIf="acc.__fx?.historicalPY"
                            class="fx-hist-cell"
                            [class.is-open]="acc.__fx?.openPY"
                            (click)="onEditHistorical(acc, 'previousYear')"
                        >
                            <span class="fx-hist-add" aria-hidden="true">+</span>
                            <span *ngIf="acc.__fx?.openPY">{{ labels.previousYearRateMissing }}</span>
                            <span *ngIf="!acc.__fx?.openPY">{{ acc.__fx?.chfPY | number : '1.2-2' }}</span>
                        </div>
                        <span
                            *ngIf="!acc.__fx?.historicalPY"
                            [class.fx-rounding-booked]="acc.__fx?.roundingPY != null"
                            [attr.title]="
                                acc.__fx?.roundingPY != null
                                    ? labels.roundingCellHint + ' ' + (acc.__fx?.roundingPY | number : '1.2-2')
                                    : null
                            "
                            >{{ acc.__fx?.chfPY | number : '1.2-2' }}</span
                        >
                    </div>
                </div>
            </ng-container>
        </ng-container>
    </ng-container>

    <!-- Reclass holding rows render like accounts; they translate at the statement default. -->
    <ng-container *ngFor="let ra of reclasses">
        <div class="grid-row account-row reclass-account-row" [style.--level]="level" *ngIf="ra != null && !ra.__fx?.hidden">
            <div class="col-name">{{ ra.name }}</div>
            <div class="col-val col-cy">{{ ra.__fx?.usdCY | number : '1.2-2' }}</div>
            <div class="col-method col-cy rate-static">{{ ra.__fx?.rateLabelCY }}</div>
            <div class="col-val col-cy">{{ ra.__fx?.chfCY | number : '1.2-2' }}</div>
            <div class="col-val col-py">{{ ra.__fx?.usdPY | number : '1.2-2' }}</div>
            <div class="col-method col-py rate-static">{{ ra.__fx?.rateLabelPY }}</div>
            <div class="col-val col-py">{{ ra.__fx?.chfPY | number : '1.2-2' }}</div>
        </div>
    </ng-container>
</ng-template>

<!-- Six blank cells so value-less rows still carry the year tint. -->
<ng-template #emptyCells>
    <div class="col-val col-cy"></div>
    <div class="col-method col-cy"></div>
    <div class="col-val col-cy"></div>
    <div class="col-val col-py"></div>
    <div class="col-method col-py"></div>
    <div class="col-val col-py"></div>
</ng-template>

<!-- The six value cells of an aggregate row: figures plus the weighted rate, never a dropdown. -->
<ng-template #aggregateCells let-fx="fx">
    <div class="col-val col-cy">{{ fx?.usdCY | number : '1.2-2' }}</div>
    <div class="col-method col-cy rate-static">{{ fx?.rateLabelCY }}</div>
    <div class="col-val col-cy">
        <ng-container *ngTemplateOutlet="aggregateAmount; context: { value: fx?.chfCY, provisional: fx?.provisionalCY }"></ng-container>
    </div>
    <div class="col-val col-py">{{ fx?.usdPY | number : '1.2-2' }}</div>
    <div class="col-method col-py rate-static">{{ fx?.rateLabelPY }}</div>
    <div class="col-val col-py">
        <ng-container *ngTemplateOutlet="aggregateAmount; context: { value: fx?.chfPY, provisional: fx?.provisionalPY }"></ng-container>
    </div>
</ng-template>

<!--
    A total that still contains unfinished historical positions is not a number yet — showing a
    partial sum would read as the real figure. The warning replaces it entirely; the tooltip says
    why.
-->
<ng-template #aggregateAmount let-value="value" let-provisional="provisional">
    <ng-container *ngIf="provisional; else amountValue">
        <ng-container *ngTemplateOutlet="warningIcon; context: { hint: labels.provisionalHint }"></ng-container>
    </ng-container>
    <ng-template #amountValue>{{ value | number : '1.2-2' }}</ng-template>
</ng-template>

<!--
    The one "this figure is not available yet" marker, used wherever a number cannot be shown.
    Same element and icon as the cashflow statement's mapping-change notice
    (mismatch-disclaimer.component.html) so the app speaks one visual language for this.
-->
<ng-template #warningIcon let-hint="hint">
    <span class="ham-icon fx-warning" [hamTooltip]="hint" [multiline]="true" tooltipPosition="left">
        <i class="exclamation-outline-icon"></i>
    </span>
</ng-template>

<!-- One Kurstyp cell: the design-system dropdown, as on the tasks page. Every account is editable. -->
<ng-template #methodCell let-year="year" let-ctrl="ctrl">
    <input
        *ngIf="ctrl"
        hamSimpleDropDown
        [options]="methodOptions[year]"
        [optionKeys]="['name']"
        returnKey="id"
        [formControl]="ctrl"
        (click)="$event.stopPropagation()"
    />
</ng-template>

<!--
    One year of the FX-difference proof. Rows are FLIs that are not entirely on the closing rate;
    each carries its CONTRIBUTION to the reserve, already signed by balance-sheet side, so the
    column adds up with a plain sum (03d §2).
-->
<ng-template #proofPanel let-proof="proof" let-current="current">
    <div class="fx-proof" *ngIf="proof">
        <div class="fx-proof-head" [class.current]="current">{{ proof.header }}</div>

        <div class="fx-proof-grid">
            <!--
                Column order is the argument each row makes, left to right: the amount, how it was
                translated, what that gave, what the closing rate would have given, and the gap that
                remains. The two label columns sit together so the six figures form one aligned block.
            -->
            <div class="fx-proof-row fx-proof-header">
                <span>{{ 'aumico_annual_report_fx_table_position' | i18next }}</span>
                <span>{{ 'aumico_annual_report_fx_table_kurstyp' | i18next }}</span>
                <span class="num">{{ functionalCurrencyName }}</span>
                <span class="num">{{ 'aumico_annual_report_fx_proof_rate_effective' | i18next }}</span>
                <span class="num">{{ displayCurrencyName }}</span>
                <span class="num">{{ 'aumico_annual_report_fx_table_method_closing' | i18next }}</span>
                <span class="num">{{ displayCurrencyName }} {{ 'aumico_annual_report_fx_proof_at_closing' | i18next }}</span>
                <span class="num">{{ 'aumico_annual_report_fx_proof_contribution' | i18next }}</span>
            </div>

            <div class="fx-proof-row" *ngFor="let row of proof.rows">
                <span class="fx-proof-label">{{ row.label }}</span>
                <span>
                    {{ row.methodLabel }}
                    <!-- A weighted rate on a mixed FLI would otherwise read as a single choice. -->
                    <em class="fx-proof-mixed" *ngIf="row.mixedDetail">{{ row.mixedDetail }}</em>
                </span>
                <span class="num">{{ row.functional | number : '1.2-2' }}</span>
                <span class="num">{{ row.rate == null ? '—' : (row.rate | number : '1.5-5') }}</span>
                <span class="num">
                    <ng-container *ngIf="row.provisional; else displayValue">
                        <ng-container *ngTemplateOutlet="warningIcon; context: { hint: labels.provisionalHint }"></ng-container>
                    </ng-container>
                    <ng-template #displayValue>{{ row.display | number : '1.2-2' }}</ng-template>
                </span>
                <span class="num">{{ proof.closingRate | number : '1.5-5' }}</span>
                <span class="num">{{ row.displayAtClosing | number : '1.2-2' }}</span>
                <span class="num fx-proof-contribution" [class.is-negative]="row.contribution < 0">
                    {{ row.provisional ? '—' : (row.contribution | number : '1.2-2') }}
                </span>
            </div>

            <div class="fx-proof-row fx-proof-sum">
                <span>{{ 'aumico_annual_report_fx_proof_sum' | i18next }}</span>
                <span></span><span></span><span></span><span></span><span></span><span></span>
                <span class="num">
                    <ng-container *ngIf="proof.provisional; else fxproofsumValue">
                        <ng-container *ngTemplateOutlet="warningIcon; context: { hint: labels.provisionalHint }"></ng-container>
                    </ng-container>
                    <ng-template #fxproofsumValue>{{ proof.sumContribution | number : '1.2-2' }}</ng-template>
                </span>
            </div>
            <div class="fx-proof-row fx-proof-rounding">
                <span>{{ 'aumico_annual_report_fx_proof_rounding' | i18next }}</span>
                <span></span><span></span><span></span><span></span><span></span><span></span>
                <span class="num">
                    <ng-container *ngIf="proof.provisional; else fxproofroundingValue">
                        <ng-container *ngTemplateOutlet="warningIcon; context: { hint: labels.provisionalHint }"></ng-container>
                    </ng-container>
                    <ng-template #fxproofroundingValue>{{ proof.rounding | number : '1.2-2' }}</ng-template>
                </span>
            </div>
            <div class="fx-proof-row fx-proof-reserve">
                <span>{{ 'aumico_annual_report_fx_table_reserve' | i18next }}</span>
                <span></span><span></span><span></span><span></span><span></span><span></span>
                <span class="num">
                    <ng-container *ngIf="proof.provisional; else fxproofreserveValue">
                        <ng-container *ngTemplateOutlet="warningIcon; context: { hint: labels.provisionalHint }"></ng-container>
                    </ng-container>
                    <ng-template #fxproofreserveValue>{{ proof.reserve | number : '1.2-2' }}</ng-template>
                </span>
            </div>

            <!-- Not a currency effect and deliberately not in the reserve (03d 2.2). -->
            <div class="fx-proof-row fx-proof-imbalance" *ngIf="proof.functionalDelta !== 0">
                <span>{{ 'aumico_annual_report_fx_proof_imbalance' | i18next }}</span>
                <span></span>
                <span class="num">{{ proof.functionalDelta | number : '1.2-2' }}</span>
                <span></span><span></span>
                <span class="num">{{ proof.closingRate | number : '1.5-5' }}</span>
                <span></span>
                <span class="num">{{ proof.functionalDelta * proof.closingRate | number : '1.2-2' }}</span>
            </div>
        </div>
    </div>
</ng-template>

<!-- Books one year's year-result rounding difference onto an income-statement account. -->
<ham-modal
    *ngIf="roundingDialog as dialog"
    [title]="'aumico_annual_report_fx_rounding_dialog_title' | i18next"
    [saveText]="'aumico_annual_report_fx_rounding_dialog_assign' | i18next"
    [showCancelButton]="true"
    [cancelText]="'aumico_button_cancel' | i18next"
    [disabled]="dialog.locked || !dialog.rounding.bookable || dialog.control.value == null"
    (save)="onRoundingAssign()"
    (cancel)="onRoundingCancel()"
>
    <!-- Divs, not <p>: the modal placeholder centres every <p> it hosts (global styles). -->
    <div class="fx-rounding-dialog">
        <div class="fx-rounding-text">{{ 'aumico_annual_report_fx_rounding_dialog_body' | i18next }}</div>
        <div class="fx-rounding-figure">
            <span>{{ 'aumico_annual_report_fx_rounding_dialog_difference' | i18next : { date: dialog.header } }}</span>
            <strong>{{ displayCurrencyName }} {{ dialog.rounding.difference | number : '1.2-2' }}</strong>
        </div>
        <div class="fx-rounding-notice is-error" *ngIf="!dialog.rounding.bookable">{{ labels.roundingTooLarge }}</div>
        <div class="fx-rounding-notice is-error" *ngIf="dialog.rounding.account != null && !dialog.rounding.accountFound">
            {{ labels.roundingAccountMissing }}
        </div>
        <div class="fx-rounding-notice" *ngIf="dialog.locked">
            {{ 'aumico_annual_report_fx_rounding_dialog_locked' | i18next }}
        </div>
        <div class="fx-rounding-field">
            <label class="fx-rounding-label">{{ 'aumico_annual_report_fx_rounding_dialog_account' | i18next }}</label>
            <input
                data-testid="fx-rounding-account-select"
                hamSimpleDropDown
                [options]="dialog.options"
                [optionKeys]="['name']"
                returnKey="id"
                [formControl]="dialog.control"
            />
        </div>
        <button
            type="button"
            class="fx-rounding-remove"
            *ngIf="dialog.rounding.account != null && !dialog.locked"
            (click)="onRoundingRemove()"
        >
            {{ 'aumico_annual_report_fx_rounding_dialog_remove' | i18next }}
        </button>
    </div>
</ham-modal>
